import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { BarChart3 } from 'lucide-react';
import { useLocation } from '@/shared/hooks/useLocation';
import { seoApi } from '../api/seoApi';
import { seoKeys } from '../queryKeys';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { PageHeader, Stat } from '@/shared/ui';
import { EmptyState } from '@/shared/ui/EmptyState';
import { SearchTermsSection } from '../components/SearchTermsSection';
import { RankingsSection } from '../components/RankingsSection';
import styles from './SEODashboardPage.module.css';

export function PerformancePage() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const navigate = useNavigate();
  const [showAll, setShowAll] = useState(false);
  const [sortCol, setSortCol] = useState<'impressions' | 'position' | 'clicks'>('impressions');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const gscQuery = useQuery({
    queryKey: integrationKeys.gscStatusShort(locationId),
    queryFn: () => integrationsApi.gsc.getStatus(locationId).then((r) => r.data),
    enabled: !!locationId,
    staleTime: 60_000,
  });

  const dailyClicksQuery = useQuery({
    queryKey: seoKeys.dailyClicks(locationId),
    queryFn: () => seoApi.getDailyClicks(locationId).then((r) => r.data),
    enabled: !!locationId && (gscQuery.data?.connected ?? false),
  });

  const keywordsQuery = useQuery({
    queryKey: seoKeys.keywords(locationId),
    queryFn: () => seoApi.getKeywords(locationId, { page: 1, limit: 2000 }),
    enabled: !!locationId && (gscQuery.data?.connected ?? false),
  });

  const gsc = gscQuery.data;
  const connected = gsc?.connected ?? false;
  const dailyClicks = dailyClicksQuery.data ?? [];
  const keywords = keywordsQuery.data?.data ?? [];

  // Position summary cards
  const positionSummary = useMemo(() => {
    const gscKeywords = keywords.filter((k) => k.avgPosition != null);
    const page1 = gscKeywords.filter((k) => k.avgPosition! <= 10).length;
    const page2_3 = gscKeywords.filter((k) => k.avgPosition! > 10 && k.avgPosition! <= 30).length;
    return { page1, page2_3, total: gscKeywords.length };
  }, [keywords]);

  const sortedKeywords = useMemo(() => {
    const sorted = [...keywords].sort((a, b) => {
      let aVal: number, bVal: number;
      if (sortCol === 'position') {
        aVal = a.avgPosition ?? 999;
        bVal = b.avgPosition ?? 999;
      } else if (sortCol === 'clicks') {
        aVal = a.clicks ?? 0;
        bVal = b.clicks ?? 0;
      } else {
        aVal = a.impressions ?? 0;
        bVal = b.impressions ?? 0;
      }
      return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
    });
    return showAll ? sorted : sorted.slice(0, 20);
  }, [keywords, showAll, sortCol, sortDir]);

  const totalClicks = dailyClicks.reduce((sum, d) => sum + d.clicks, 0);
  const maxClicks = Math.max(...dailyClicks.map((d) => d.clicks), 1);

  // Trend vs prior
  let trendPct: number | null = null;
  if (gsc?.totalClicks != null && gsc.prevTotalClicks != null && gsc.prevTotalClicks > 0) {
    trendPct = Math.round(((gsc.totalClicks - gsc.prevTotalClicks) / gsc.prevTotalClicks) * 100);
  }

  if (!connected) {
    return (
      <div className={styles.page}>
        <PageHeader title="Performance" subtitle="How patients find you on Google" />
        <EmptyState
          icon={<BarChart3 size={40} strokeWidth={1.5} aria-hidden />}
          title="Connect Google Search Console"
          description="To see how patients find you on Google — search terms, click data, and ranking positions — connect your Google Search Console account."
          action={{
            label: 'Connect Google Search Console',
            onClick: () => navigate('/settings/integrations/google-search-console/setup'),
          }}
        />
        <RankingsSection locationId={locationId} />
        <SearchTermsSection locationId={locationId} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader title="Performance" subtitle="How patients find you on Google" />

      {dailyClicksQuery.isLoading ? (
        <SkeletonCard />
      ) : (
        <>
          {/* Traffic Chart */}
          {dailyClicks.length > 0 && (
            <div className={styles.trafficCard}>
              <div className={styles.trafficHeader}>
                <div>
                  <div className={styles.trafficTitle}>Google Search Clicks</div>
                  <div className={styles.trafficSub}>Last 28 days — daily</div>
                </div>
                <div className={styles.trafficSummary}>
                  <span className={styles.trafficTotal}>{totalClicks.toLocaleString()}</span>
                  <span className={styles.trafficTotalLabel}>total</span>
                  {trendPct != null && (
                    <div className={trendPct >= 0 ? styles.trendUp : styles.trendDown}>
                      {trendPct >= 0 ? '+' : ''}
                      {trendPct}% vs prior
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.barChart}>
                {dailyClicks.map((d, i) => (
                  <div
                    key={i}
                    className={clsx(styles.bar, i >= dailyClicks.length - 7 && styles.barRecent)}
                    style={{ height: `${(d.clicks / maxClicks) * 100}%` }}
                  />
                ))}
              </div>
              <div className={styles.barAxis}>
                <span>28 days ago</span>
                <span>Today</span>
              </div>
            </div>
          )}

          {/* Position Summary Cards */}
          <div className={styles.statGrid}>
            {[
              {
                label: 'On page 1 of Google',
                value: positionSummary.page1,
                sub: 'search terms (position 1-10)',
              },
              {
                label: 'On pages 2-3',
                value: positionSummary.page2_3,
                sub: 'search terms (position 11-30)',
              },
              {
                label: 'Total visible',
                value: positionSummary.total,
                sub: 'search terms you appear for',
              },
            ].map((card, i) => (
              <Stat key={i} card size="lg" label={card.label} value={card.value} sub={card.sub} />
            ))}
          </div>

          {/* Keywords Table */}
          {keywordsQuery.isLoading ? (
            <SkeletonCard />
          ) : (
            keywords.length > 0 && (
              <div className={styles.perfKeywordsCard}>
                <div className={styles.trafficTitle}>What Patients Search to Find You</div>
                <div className={styles.perfKeywordsSub}>
                  Top {showAll ? keywords.length : Math.min(20, keywords.length)} by{' '}
                  {sortCol === 'position'
                    ? 'position'
                    : sortCol === 'clicks'
                      ? 'clicks'
                      : 'impressions'}
                </div>

                <div className={styles.perfKwHead}>
                  <span>Search Term</span>
                  {(
                    [
                      ['position', 'Position'],
                      ['impressions', 'Seen By'],
                      ['clicks', 'Clicks'],
                    ] as const
                  ).map(([col, label]) => (
                    <span
                      key={col}
                      onClick={() => {
                        if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                        else {
                          setSortCol(col);
                          setSortDir(col === 'position' ? 'asc' : 'desc');
                        }
                      }}
                      className={clsx(
                        styles.perfKwHeadCell,
                        sortCol === col && styles.perfKwHeadCellActive,
                      )}
                    >
                      {label}
                      {sortCol === col ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                    </span>
                  ))}
                </div>

                {sortedKeywords.map((k, i) => {
                  const pos = k.avgPosition != null ? Math.round(k.avgPosition) : null;
                  const posClass =
                    pos == null
                      ? styles.statValueMuted
                      : pos <= 3
                        ? styles.statValueSuccess
                        : pos <= 10
                          ? styles.perfKwPosAccent
                          : pos <= 20
                            ? styles.statValueWarning
                            : styles.statValueMuted;
                  const isOpportunity =
                    !k.isBranded &&
                    k.avgPosition != null &&
                    k.avgPosition >= 4 &&
                    k.avgPosition <= 20;

                  return (
                    <div
                      key={k.id}
                      className={clsx(
                        styles.perfKwRow,
                        i === sortedKeywords.length - 1 && styles.perfKwRowLast,
                      )}
                    >
                      <span className={styles.perfKwQuery}>
                        {k.query}
                        {isOpportunity && (
                          <span className={clsx(styles.miniBadge, styles.miniBadgeOpportunity)}>
                            opportunity
                          </span>
                        )}
                      </span>
                      <div className={styles.perfKwPosCell}>
                        <span className={clsx(styles.perfKwPos, posClass)}>
                          {pos != null ? `#${pos}` : '—'}
                        </span>
                      </div>
                      <span className={styles.perfKwCell}>
                        {k.impressions != null ? k.impressions.toLocaleString() : '—'}
                      </span>
                      <span
                        className={clsx(
                          styles.perfKwCell,
                          (k.clicks ?? 0) > 0 && styles.perfKwCellHighlight,
                        )}
                      >
                        {k.clicks != null ? k.clicks.toLocaleString() : '—'}
                      </span>
                    </div>
                  );
                })}

                {!showAll && keywords.length > 20 && (
                  <button
                    type="button"
                    onClick={() => setShowAll(true)}
                    className={styles.perfShowAll}
                  >
                    Show all {keywords.length} keywords
                  </button>
                )}

                <div className={styles.perfFootnote}>
                  Position = where you appear in Google results (1-10 = page 1, lower is better).
                  Seen By = how many patients saw your listing in the last 28 days.
                </div>
              </div>
            )
          )}
        </>
      )}

      <RankingsSection locationId={locationId} />
      <SearchTermsSection locationId={locationId} />
    </div>
  );
}
